import { getLogger } from '@trimble/logging';
import { LoadQueue, LoadQueueOptions } from './load-queue.types';

interface QueueTask {
  operation(): Promise<void>;
  key?: string;
  seq: number;
}

const DEFAULT_CONCURRENCY = 1;

export function createLoadQueue(options?: LoadQueueOptions): LoadQueue {
  const concurrency = options?.concurrency ?? DEFAULT_CONCURRENCY;

  const pending: QueueTask[] = [];
  const runningKeys = new Set<string>();
  let runningCount = 0;
  let runningBarrier = false;
  let lastSeq = 0;
  // Sequence numbers of every operation that has been enqueued but not yet
  // completed (still pending or running). Tracking individual operations rather
  // than a global completion count ensures drain() only resolves once all work
  // that existed at call time is done, regardless of out-of-order completion of
  // operations enqueued afterwards.
  const outstanding = new Set<number>();
  const drainWaiters: Array<{ target: number; resolve(): void }> = [];

  const minOutstanding = (): number => {
    let min = Infinity;
    for (const seq of outstanding) {
      if (seq < min) min = seq;
    }
    return min;
  };

  const notifyDrainWaiters = (): void => {
    const min = minOutstanding();
    for (let i = drainWaiters.length - 1; i >= 0; i--) {
      if (drainWaiters[i].target < min) {
        const [waiter] = drainWaiters.splice(i, 1);
        waiter.resolve();
      }
    }
  };

  const schedule = (): void => {
    while (runningCount < concurrency && !runningBarrier) {
      const head = pending[0];
      if (!head) break;

      let taskIndex = -1;
      if (head.key === undefined) {
        if (runningCount === 0) {
          taskIndex = 0;
        }
      } else {
        for (let i = 0; i < pending.length; i++) {
          const candidate = pending[i];
          if (candidate.key === undefined) break;
          if (!runningKeys.has(candidate.key)) {
            taskIndex = i;
            break;
          }
        }
      }

      if (taskIndex === -1) break;

      const [task] = pending.splice(taskIndex, 1);
      runningCount++;
      if (task.key === undefined) {
        runningBarrier = true;
      } else {
        runningKeys.add(task.key);
      }

      task
        .operation()
        .catch((error) => getLogger().error('Error in queued operation', error))
        .finally(() => {
          runningCount--;
          outstanding.delete(task.seq);
          if (task.key === undefined) {
            runningBarrier = false;
          } else {
            runningKeys.delete(task.key);
          }
          schedule();
          notifyDrainWaiters();
        });
    }
  };

  return {
    enqueue(operation: () => Promise<void>, key?: string): void {
      const seq = ++lastSeq;
      outstanding.add(seq);
      pending.push({ operation, key, seq });
      schedule();
    },
    drain(): Promise<void> {
      // Capture the latest enqueued sequence: resolve once every operation up to
      // and including this one has completed. Operations enqueued after this
      // point get higher sequence numbers and are intentionally not awaited.
      const target = lastSeq;
      if (target < minOutstanding()) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve) => {
        drainWaiters.push({ target, resolve });
      });
    },
    clear(): void {
      for (const task of pending) {
        outstanding.delete(task.seq);
      }
      pending.length = 0;
      notifyDrainWaiters();
    },
  };
}
